import { I } from '../lib/icons';

/**
 * Kennzeichnung „KI generiert" (Transparenzpflicht ab 2026-08-01).
 *
 * Eine Komponente für beide Fundstellen — Thumbnail-Overlay und Detailseite —
 * damit Wortlaut und Aussehen nicht auseinanderlaufen. Der Text steht in
 * `AI_GENERATED_LABEL` (server-lib) bzw. hier gespiegelt als Konstante; er ist
 * bewusst kurz und ohne Abkürzung („KI generiert", nicht „KI").
 *
 * ⚠️ Der Overlay-Hintergrund ist **deckend**, nicht transparent wie das
 * Stapel-Badge daneben. Über einem hellen oder unruhigen Bild wäre eine
 * halbtransparente Fläche stellenweise unlesbar — bei einer Pflichtangabe ist
 * „meistens lesbar" kein akzeptabler Zustand. Der kleine optische Bruch zum
 * Stapel-Badge ist bewusst in Kauf genommen.
 *
 * ⚠️ Dazu ein heller Hairline-Ring. Deckend allein löst nur die helle Hälfte
 * des Problems: über einem DUNKLEN Motiv verschmilzt die dunkle Pill-Fläche mit
 * dem Hintergrund, die Kante verschwindet und der Text wirkt wie frei
 * schwebende Schrift statt wie eine Kennzeichnung (im lokalen Check auf einer
 * dunklen Video-Kachel genau so aufgetreten). Der Ring garantiert die
 * Abgrenzung in beide Richtungen — hell wie dunkel.
 */

export const AI_GENERATED_LABEL = 'KI generiert';

type Props = {
  /**
   * Die Variante benennt den ORT, nicht nur den Stil — die drei Fundstellen
   * unterscheiden sich sowohl in der Positionierung als auch darin, ob der
   * Wortlaut mitläuft:
   *
   * - `thumbnail` — Overlay auf der Grid-Kachel. **Nur Icon** (User-Wunsch
   *   2026-07-30): auf kleinen Kacheln verdeckt die Beschriftung zu viel vom
   *   Motiv. Der Wortlaut bleibt über `title`/`aria-label` erreichbar.
   * - `detail` — Overlay auf dem großen Bild der Detailseite. **Mit Text** —
   *   hier ist Platz, und es ist die Ansicht, in der man das Asset prüft.
   * - `inline` — im Textfluss, mit Text.
   *
   * ⚠️ `thumbnail` und `detail` teilten sich anfangs eine gemeinsame
   * `overlay`-Variante. Beim Icon-only-Umbau nahm das der Detailseite
   * stillschweigend den Text mit. Deshalb getrennt benannt: wer künftig eine
   * der beiden ändert, ändert nicht ungewollt die andere.
   */
  variant?: 'thumbnail' | 'detail' | 'inline';
};

export function AiGeneratedPill({ variant = 'inline' }: Props) {
  const isOverlay = variant === 'thumbnail' || variant === 'detail';
  const showLabel = variant !== 'thumbnail';

  // ring-1 + helle Ring-Farbe: hält die Pill-Kante auch auf dunklen Motiven
  // sichtbar (s. Kommentar oben). Auf hellem Grund fällt der Ring nicht auf.
  const base =
    'inline-flex items-center rounded-md font-semibold whitespace-nowrap ' +
    'bg-brand-green-900 text-brand-cream ring-1 ring-brand-cream/40';

  // pointer-events-none: darf Card-Klick und Auswahl nicht abfangen (gleiches
  // Muster wie Play- und Stapel-Overlay).
  const position = 'pointer-events-none absolute bottom-2 left-2 z-10 shadow-sm';

  const className = isOverlay
    ? showLabel
      ? `${base} ${position} gap-1 px-2 py-1 text-xs`
      : // Symmetrisches Padding, damit das Icon-only-Pill rund statt gequetscht wirkt.
        `${base} ${position} p-1`
    : `${base} gap-1 px-2 py-1 text-xs`;

  return (
    <span
      className={className}
      data-ai-generated
      // Ohne Text braucht die Kennzeichnung einen zugänglichen Namen: `title`
      // liefert den Tooltip beim Drüberfahren, `aria-label` + role="img" die
      // Ansage für Screenreader. Sonst wäre die Pflichtangabe im Thumbnail für
      // beide Wege verschwunden.
      {...(showLabel
        ? {}
        : { title: AI_GENERATED_LABEL, role: 'img' as const, 'aria-label': AI_GENERATED_LABEL })}
    >
      <I.sparkle size={13} aria-hidden />
      {showLabel && AI_GENERATED_LABEL}
    </span>
  );
}
